import React, { useState, useRef } from 'react';
import Select from "react-select";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Icon,
  useToast,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Tooltip,
  Badge,
  Flex,
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiX, FiTrash2,FiEdit } from 'react-icons/fi';
import axios from 'axios';
import config from '../../config'; // Adjust import path as needed
import AddAndUpdateClientBranchModal from './AddAndUpdateClientBranchModal';
const FileUploadModal = ({
  isOpen,
  onClose,
  clientId,
  fetchUpdate,
  isExport,
  client,
  clientAllContacts,
  userId,
  userRole,
  candidate_ownerName,
  accountName,
  contactPersons,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedData, setUploadedData] = useState([]);
  const [showDataTable, setShowDataTable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedContactPerson, setSelectedContactPerson] = useState(null);
  const fileInputRef = useRef(null);
  const toast = useToast();

  // Theme color
  const primaryColor = "#083c64";
  const [showAddBranch, setShowAddBranch] = useState(false);
  const handleShowAddBranch = () => {
    setShowAddBranch(true);
  };
  const handleAddBranchClose = () => {
    setShowAddBranch(false);
  };
  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  // Handle browse button click
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file removal
  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUploading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("clientId", clientId);

      // Upload file to backend
      const response = await axios.post(
        `${config?.apiUrl}/api/upload_excel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response && response.data) {
        // Extract data from response
        const extractedData =
          response.data.data || response.data.records || response.data;
        if(extractedData.length === 0){
          toast({
          title: "Upload successful",
          description: `File don't have any data or the branch already exist in the client`,
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return
        }  

        // Ensure it's an array
        if (Array.isArray(extractedData)) {
         
          setUploadedData(
            extractedData.map((data) => ({
              ...data,
              isMainBranch: data.isMainBranch == "Yes" || " yes" ? true : false,
            }))
          );
          setShowDataTable(true);
        } else {
          const dataArray = Object.values(extractedData).find((val) =>
            Array.isArray(val)
          );
          if (dataArray) {
            setUploadedData(dataArray);
            setShowDataTable(true);
          } else {
            throw new Error("Invalid data format received");
          }
        }
        fetchUpdate();
        toast({
          title: "Upload successful",
          description: `${extractedData.length} records uploaded successfully.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Reset file selection
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description:
          error.response?.data?.message ||
          "There was an error uploading your file. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle save data to backend
  const handleSaveData = async () => {
    if (uploadedData.length === 0) {
      toast({
        title: "No data to save",
        description: "Please upload a file first.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);

    try {
      const response = await axios.post(
        `${config?.apiUrl}/api/save-excel-data`,
        {
          branches: uploadedData,
          clientId: clientId,
        }
      );

      if (response && response.data) {
        toast({
          title: "Data saved successfully",
          description: `${uploadedData.length} records have been saved.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Refresh parent component
        fetchUpdate();

        // Close modal after successful save
        setTimeout(() => {
          handleClose();
        }, 500);
      } else {
        throw new Error("Save failed");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Save failed",
        description:
          error.response?.data?.message ||
          "There was an error saving your data. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle row deletion
  const handleDeleteRow = async (index, row) => {
    try {
      const newData = uploadedData.filter((_, i) => i !== index);
      setUploadedData(newData);
      if (newData.length === 0) {
        setShowDataTable(false);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Delete failed",
        description:
          error.response?.data?.message ||
          "There was an error deleting the document.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      setShowDataTable(false);
      setUploadedData([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onClose();
    }
  };

  // Back to upload view
  const handleBackToUpload = () => {
    setShowDataTable(false);
    setUploadedData([]);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Format cell value for display
  const formatCellValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  // Get city from address object
  const getCity = (row) => {
    if (row.address && row.address.city) {
      return row.address.city;
    }
    return row.city || "-";
  };

  // Define columns to display in order
  const tableColumns = [
    { key: "branchCode", label: "Branch Code" },
    { key: "branchName", label: "Branch Name" },
    { key: "recuiterName", label: "Recruiter Name" },
    { key: "phone", label: "Phone" },
    { key: "city", label: "City", customRender: getCity },
    { key: "status", label: "Status" },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={showDataTable ? "6xl" : "lg"}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(4px)" />
      <ModalContent maxH="85vh" borderRadius="xl" boxShadow="xl">
        <ModalHeader
          borderBottom="1px solid"
          borderColor="gray.100"
          color={primaryColor}
          fontSize="lg"
          fontWeight="600"
        >
          {showDataTable ? "Uploaded Data" : "Upload File"}
        </ModalHeader>
        <ModalCloseButton isDisabled={isUploading} color={primaryColor} />

        <ModalBody overflowY="auto" p={6}>
          {!showDataTable ? (
            /* UPLOAD VIEW */
            <VStack spacing={4}>
              {/* Drag and Drop Zone */}
              <Box
                border="2px dashed"
                borderColor={isDragOver ? primaryColor : "gray.300"}
                borderRadius="xl"
                p={10}
                textAlign="center"
                bg={isDragOver ? "blue.50" : "gray.50"}
                transition="all 0.3s ease"
                cursor="pointer"
                w="100%"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleBrowseClick}
                _hover={{ borderColor: primaryColor, bg: "gray.100" }}
              >
                <VStack spacing={4}>
                  <Icon as={FiUpload} boxSize={12} color="gray.400" />
                  <Text fontSize="md" fontWeight="500" color="gray.700">
                    Drag and drop your file here
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    or click to browse
                  </Text>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBrowseClick();
                    }}
                    bg={primaryColor}
                    color="white"
                    borderColor={primaryColor}
                    _hover={{ bg: "#0a4d7d", borderColor: "#0a4d7d" }}
                    borderRadius="md"
                  >
                    Browse Files
                  </Button>
                </VStack>
              </Box>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileInputChange}
                style={{ display: "none" }}
                accept=".xlsx,.xls,.csv"
              />

              {/* Selected file display */}
              {selectedFile && (
                <Box
                  w="100%"
                  p={4}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                  bg="white"
                  boxShadow="sm"
                >
                  <HStack justify="space-between">
                    <HStack spacing={3}>
                      <Box bg="blue.50" p={2} borderRadius="md">
                        <Icon as={FiFile} color={primaryColor} boxSize={5} />
                      </Box>
                      <VStack align="start" spacing={0}>
                        <Text
                          fontSize="sm"
                          fontWeight="500"
                          color="gray.700"
                          noOfLines={1}
                        >
                          {selectedFile.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {formatFileSize(selectedFile.size)}
                        </Text>
                      </VStack>
                    </HStack>
                    <IconButton
                      icon={<FiX />}
                      size="sm"
                      variant="ghost"
                      onClick={handleRemoveFile}
                      isDisabled={isUploading}
                      aria-label="Remove file"
                      color="gray.500"
                      _hover={{ bg: "red.50", color: "red.500" }}
                    />
                  </HStack>
                </Box>
              )}
            </VStack>
          ) : (
            /* DATA TABLE VIEW */
            <Box>
              <HStack
                justify="space-between"
                mb={4}
                p={3}
                bg="gray.50"
                borderRadius="lg"
              >
                <Text fontSize="sm" fontWeight="500" color="gray.700">
                  {uploadedData.length}{" "}
                  {uploadedData.length === 1 ? "record" : "records"} found
                </Text>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    onClick={handleSaveData}
                    isLoading={isSaving}
                    loadingText="Saving..."
                    bg={primaryColor}
                    color="white"
                    _hover={{ bg: "#0a4d7d" }}
                    _active={{ bg: "#062c48" }}
                    borderRadius="md"
                    leftIcon={<Icon as={FiFile} />}
                  >
                    Save Data
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBackToUpload}
                    borderColor={primaryColor}
                    color={primaryColor}
                    _hover={{ bg: primaryColor, color: "white" }}
                    borderRadius="md"
                  >
                    Upload Another File
                  </Button>

                  <Select 
                    name="contactPerson"
                    options={clientAllContacts.map((client) => ({
                      label:
                        client.accountOwnerName ||
                        client.clientName ||
                        client.label,
                      value: client._id || client.value,
                    }))}
                    width="200px"
                    placeholder="Select contact person"
                    value={selectedContactPerson}
                    onChange={(selected) => {
                      if (selected && selected.value) {
                        setUploadedData(
                          uploadedData.map((data) => {
                            return { ...data, contactPersonId: selected.value };
                          })
                        );
                      }
                      setSelectedContactPerson(selected);
                    }}
                    isClearable
                  />
                </HStack>
              </HStack>

              {uploadedData.length > 0 ? (
                <TableContainer
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="gray.200"
                  maxH={uploadedData.length <= 3 ? "auto" : "400px"}
                  overflowY={uploadedData.length > 3 ? "auto" : "visible"}
                >
                  <Table variant="simple" size="sm">
                    <Thead
                      bg={primaryColor}
                      position="sticky"
                      top={0}
                      // zIndex={1}
                    >
                      <Tr>
                        <Th
                          color="white"
                          fontSize="xs"
                          fontWeight="600"
                          textTransform="uppercase"
                          borderBottom="none"
                        >
                          #
                        </Th>
                        {tableColumns.map((column) => (
                          <Th
                            key={column.key}
                            color="white"
                            fontSize="xs"
                            fontWeight="600"
                            textTransform="uppercase"
                            borderBottom="none"
                          >
                            {column.label}
                          </Th>
                        ))}
                        <Th
                          textAlign="center"
                          width="80px"
                          color="white"
                          fontSize="xs"
                          fontWeight="600"
                          textTransform="uppercase"
                          borderBottom="none"
                        >
                          Actions
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {uploadedData.map((row, index) => (
                        <Tr
                          key={index}
                          _hover={{ bg: "gray.50" }}
                          borderBottom="1px solid"
                          borderColor="gray.100"
                        >
                          <Td fontWeight="500" color="gray.600" fontSize="sm">
                            {index + 1}
                          </Td>
                          {tableColumns.map((column) => (
                            <Td key={column.key} fontSize="sm" color="gray.700">
                              {column.key === "status" ? (
                                <Badge
                                  colorScheme={
                                    row[column.key]?.toLowerCase() === "active"
                                      ? "green"
                                      : "red"
                                  }
                                  fontSize="xs"
                                  px={2}
                                  py={1}
                                  borderRadius="md"
                                >
                                  {formatCellValue(row[column.key])}
                                </Badge>
                              ) : column.customRender ? (
                                column.customRender(row)
                              ) : (
                                formatCellValue(row[column.key])
                              )}
                            </Td>
                          ))}
                          <Td textAlign="center">
                            <Flex
                              gap={2}
                              justify="center"
                              alignContent={"center"}
                            >
                              <div>
                                <Tooltip
                                  label="Delete record"
                                  hasArrow
                                  placement="left"
                                >
                                  <IconButton
                                    icon={<FiTrash2 />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteRow(index, row)}
                                    aria-label="Delete document"
                                    color="gray.500"
                                    _hover={{ bg: "red.50", color: "red.500" }}
                                  />
                                </Tooltip>
                              </div>

                              <div>
                                <Tooltip
                                  label="Edit record"
                                  hasArrow
                                  placement="left"
                                >
                                  <IconButton
                                    icon={<FiEdit />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleShowAddBranch()}
                                    aria-label="Edit document"
                                    color="gray.500"
                                    _hover={{
                                      bg: "green.50",
                                      color: "green.500",
                                    }}
                                  />
                                </Tooltip>
                                <AddAndUpdateClientBranchModal
                                  contactPersons={clientAllContacts?.map(
                                    (el) => ({
                                      label: el?.accountOwnerName,
                                      value: el?._id,
                                    })
                                  )}
                                  alreadyPresentData={row}
                                  client={client}
                                  isOpen={showAddBranch}
                                  onClose={handleAddBranchClose}
                                  fetchAllBranches={async () => {
                                    await fetchUpdate();
                                    handleDeleteRow(index, row)
                                  }}
                                  userId={userId}
                                  userRole={userRole}
                                  candidate_ownerName={candidate_ownerName}
                                  accountName={accountName}
                                />
                              </div>
                            </Flex>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              ) : (
                <Box textAlign="center" py={10} bg="gray.50" borderRadius="lg">
                  <Text color="gray.500" fontSize="sm">
                    No data to display
                  </Text>
                </Box>
              )}
            </Box>
          )}
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.100">
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={isUploading}
            color="gray.600"
            _hover={{ bg: "gray.100" }}
          >
            {showDataTable ? "Close" : "Cancel"}
          </Button>
          {!showDataTable && (
            <Button
              onClick={handleUpload}
              isLoading={isUploading}
              loadingText="Uploading..."
              spinner={<Spinner size="sm" />}
              isDisabled={!selectedFile}
              bg={primaryColor}
              color="white"
              _hover={{ bg: "#0a4d7d" }}
              _active={{ bg: "#062c48" }}
              borderRadius="md"
            >
              Upload
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FileUploadModal;